import type { MedicationResponse } from '@emr/shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { listMedications } from '../../../api/chart.ts';
import type { DataTableColumn } from '../../../components/DataTable.tsx';
import { formatCalendarDate } from '../../../lib/dateFormat.ts';
import { ChartTable } from './ChartTable.tsx';

// `name`, `dose`, `route`, `frequency`, and `status` are all open free text (`MAX_NAME_LENGTH`),
// not closed enums — every one gets `maxWidthCh`, per `DataTable.tsx`'s own header comment on why
// the wrap class alone does not constrain a table column by itself.
// Tighter than the 4-column tabs' own caps (`ProblemsTab`, `AllergiesTab`): seven columns share the
// same ~51.5rem desktop ceiling, and seven lots of `px-3` cell padding (10.5rem) alone eat into that
// budget before any content width is counted at all.
function columnsFor(
  t: TFunction<'patientChartTabs'>,
): readonly DataTableColumn<MedicationResponse>[] {
  return [
    {
      key: 'name',
      header: t('medications.columns.name'),
      cell: (medication) => medication.name,
      wrap: 'anywhere',
      maxWidthCh: 18,
    },
    {
      key: 'dose',
      header: t('medications.columns.dose'),
      cell: (medication) => medication.dose ?? '—',
      wrap: 'wrap',
      maxWidthCh: 10,
    },
    {
      key: 'route',
      header: t('medications.columns.route'),
      cell: (medication) => medication.route ?? '—',
      wrap: 'wrap',
      maxWidthCh: 10,
    },
    {
      key: 'frequency',
      header: t('medications.columns.frequency'),
      cell: (medication) => medication.frequency ?? '—',
      wrap: 'wrap',
      maxWidthCh: 12,
    },
    {
      key: 'status',
      header: t('medications.columns.status'),
      cell: (medication) => medication.status,
      wrap: 'wrap',
      maxWidthCh: 10,
    },
    {
      key: 'start',
      header: t('medications.columns.start'),
      cell: (medication) =>
        medication.startDate === null ? '—' : formatCalendarDate(medication.startDate),
      wrap: 'nowrap',
    },
    {
      key: 'end',
      header: t('medications.columns.end'),
      cell: (medication) =>
        medication.endDate === null ? '—' : formatCalendarDate(medication.endDate),
      wrap: 'nowrap',
    },
  ];
}

export function MedicationsTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');
  return (
    <ChartTable
      queryKey={['patient', patientId, 'medications']}
      queryFn={(signal) => listMedications(patientId, signal)}
      columns={columnsFor(t)}
      rowKey={(medication) => medication.id}
      // Was 60 — well past `AppShell`'s actual desktop content-width ceiling (~51.5rem: `max-w-6xl`
      // minus the nav rail, the gap, the page's own padding, and the Panel's own padding), so the
      // table structurally overflowed its container on every desktop viewport, not just narrow
      // ones. 44 matches `ProblemsTab`/`VitalsTab`, comfortably under that ceiling.
      minWidthRem={44}
      emptyMessage={t('medications.empty')}
      loadingLabel={t('medications.loading')}
      caption={t('medications.caption')}
    />
  );
}
